import { Head, router, useForm } from "@inertiajs/react"
import type { FormEventHandler } from "react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}

interface TimeEntryData {
  user_id: number | null
  time_type: string
  started_at: string | null
  ended_at: string | null
  admin_minutes: number
  service_item_type_id: number | null
  travel_hours: number
  mileage_on_job: number
  vehicle_allowance: string
  notes: string
}

interface Props {
  activity: Activity
  time_entry: TimeEntryData
  users: { id: number; name: string }[]
  service_item_types: { id: number; name: string }[]
  can_select_employee: boolean
  can_use_block_time: boolean
  errors?: Record<string, string[]>
}

export default function New({
  activity,
  time_entry,
  users,
  service_item_types,
  can_select_employee,
  can_use_block_time,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Time",
      href: `/marketing_activities/${activity.id}/time_entries`,
    },
    {
      title: "New",
      href: `/marketing_activities/${activity.id}/time_entries/new`,
    },
  ]

  const { data, setData, post, processing, transform } = useForm<TimeEntryData>(
    {
      user_id: time_entry.user_id,
      time_type: time_entry.time_type,
      started_at: time_entry.started_at,
      ended_at: time_entry.ended_at,
      admin_minutes: time_entry.admin_minutes,
      service_item_type_id: time_entry.service_item_type_id,
      travel_hours: time_entry.travel_hours,
      mileage_on_job: time_entry.mileage_on_job,
      vehicle_allowance: time_entry.vehicle_allowance || "",
      notes: time_entry.notes,
    },
  )

  transform((data) => ({ case_time_entry: data }))

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    post(`/marketing_activities/${activity.id}/time_entries`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"
  const errorClass = "mt-1 text-sm text-hpi-red"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Time - ${activity.title}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Time Entry</h1>

        <div className="rounded-lg bg-white p-6 shadow">
          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-4">
                {can_select_employee && (
                  <div>
                    <label className={labelClass}>Employee</label>
                    <select
                      className={inputClass}
                      value={data.user_id ?? ""}
                      onChange={(e) =>
                        setData(
                          "user_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      required
                    >
                      <option value="">Select employee...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </select>
                    {errors?.user_id && (
                      <p className={errorClass}>{errors.user_id[0]}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className={labelClass}>Time Type</label>
                  <select
                    className={inputClass}
                    value={data.time_type}
                    onChange={(e) => setData("time_type", e.target.value)}
                    required
                  >
                    <option value="Actual">Actual (Start/End)</option>
                    {can_use_block_time && (
                      <option value="Block">Block (Hours/Minutes)</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Service Type</label>
                  <select
                    className={inputClass}
                    value={data.service_item_type_id ?? ""}
                    onChange={(e) =>
                      setData(
                        "service_item_type_id",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    required
                  >
                    <option value="">Select service type...</option>
                    {service_item_types.map((si) => (
                      <option key={si.id} value={si.id}>
                        {si.name}
                      </option>
                    ))}
                  </select>
                  {errors?.service_item_type_id && (
                    <p className={errorClass}>
                      {errors.service_item_type_id[0]}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {data.time_type === "Actual" ? (
                  <>
                    <div>
                      <label className={labelClass}>Start OTJ</label>
                      <input
                        type="datetime-local"
                        className={inputClass}
                        value={data.started_at ?? ""}
                        onChange={(e) => setData("started_at", e.target.value)}
                        required
                      />
                      {errors?.started_at && (
                        <p className={errorClass}>{errors.started_at[0]}</p>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>End OTJ</label>
                      <input
                        type="datetime-local"
                        className={inputClass}
                        value={data.ended_at ?? ""}
                        onChange={(e) => setData("ended_at", e.target.value)}
                        required
                      />
                      {errors?.ended_at && (
                        <p className={errorClass}>{errors.ended_at[0]}</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className={labelClass}>Date</label>
                      <input
                        type="date"
                        className={inputClass}
                        value={
                          data.started_at
                            ? data.started_at.substring(0, 10)
                            : ""
                        }
                        onChange={(e) => {
                          setData("started_at", e.target.value + "T00:00")
                          setData("ended_at", e.target.value + "T00:00")
                        }}
                        required
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Block Time (minutes)</label>
                      <input
                        type="number"
                        min="0"
                        step="15"
                        className={inputClass}
                        value={data.admin_minutes || ""}
                        onChange={(e) =>
                          setData(
                            "admin_minutes",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        required
                      />
                      {errors?.admin_minutes && (
                        <p className={errorClass}>{errors.admin_minutes[0]}</p>
                      )}
                    </div>
                  </>
                )}

                <div>
                  <label className={labelClass}>Travel (hours)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.25"
                    className={inputClass}
                    value={data.travel_hours || ""}
                    onChange={(e) =>
                      setData("travel_hours", parseFloat(e.target.value) || 0)
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <label className={labelClass}>Explain Time</label>
              <textarea
                className={inputClass}
                rows={4}
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                required
              />
              {errors?.notes && <p className={errorClass}>{errors.notes[0]}</p>}
            </div>

            <fieldset className="rounded-md border p-4">
              <legend className="text-hpi-dark-gray px-2 text-sm font-medium">
                Mileage{" "}
                <span className="text-hpi-med-gray font-normal">
                  leave blank if no mileage
                </span>
              </legend>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className={labelClass}>Mileage</label>
                  <input
                    type="number"
                    min="0"
                    className={inputClass}
                    value={data.mileage_on_job || ""}
                    onChange={(e) =>
                      setData("mileage_on_job", parseInt(e.target.value) || 0)
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>Mileage Reimbursement</label>
                  <select
                    className={inputClass}
                    value={data.vehicle_allowance}
                    onChange={(e) =>
                      setData("vehicle_allowance", e.target.value)
                    }
                    required
                  >
                    <option disabled value="">
                      --- SELECT ---
                    </option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                  {errors?.vehicle_allowance && (
                    <p className={errorClass}>{errors.vehicle_allowance[0]}</p>
                  )}
                </div>
              </div>
            </fieldset>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() =>
                  router.get(
                    `/marketing_activities/${activity.id}/time_entries`,
                  )
                }
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Add Time Entry"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
